import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/joy'
import {
  Plus as AddIcon,
  ClockCounterClockwise as HistoryIcon,
  ChartBar as AssessmentIcon,
  Image as ImageIcon,
} from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

interface AnalysisStats {
  total_analyses: number
  by_status: { [key: string]: number }
  average_severity: number | null
  total_detections: number
  detections_by_category: { [key: string]: number }
}

export const HawkeyeDashboard: React.FC = () => {
  const navigate = useNavigate()

  // Fetch statistics
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['hawkeye-stats'],
    queryFn: async () => {
      const response = await api.get<AnalysisStats>('/api/hawkeye/analyses/statistics/')
      return response.data
    },
    retry: false, // Don't retry if endpoint is unavailable
  })

  const getSeverityColor = (score: number | null) => {
    if (score === null) return 'neutral'
    if (score < 0.3) return 'success'
    if (score < 0.6) return 'warning'
    return 'danger'
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success'
      case 'processing': return 'primary'
      case 'failed': return 'danger'
      case 'queued': return 'warning'
      default: return 'neutral'
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography level="h2" sx={{ mb: 1 }}>
            Hawkeye™ Image Analysis
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            AI-powered roof damage detection and analysis
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startDecorator={<AddIcon />}
            onClick={() => navigate('/hawkeye/analyze')}
            disabled={!!error}
          >
            New Analysis
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<HistoryIcon />}
            onClick={() => navigate('/hawkeye/history')}
            disabled={!!error}
          >
            View History
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert color="warning" sx={{ mb: 3 }}>
          <Box>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Hawkeye™ Coming Soon
            </Typography>
            <Typography level="body-sm">
              The Hawkeye AI-powered roof damage detection system is currently being prepared with training data.
              This feature will be available soon. In the meantime, you can continue using other ApexOS features.
            </Typography>
          </Box>
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <Grid container spacing={3}>
          {/* Total Analyses Card */}
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ImageIcon size={40} color="var(--joy-palette-primary-500)" />
                </Box>
                <Typography level="h3">{stats?.total_analyses || 0}</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Total Analyses
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Detections Card */}
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssessmentIcon size={40} color="var(--joy-palette-warning-500)" />
                </Box>
                <Typography level="h3">{stats?.total_detections || 0}</Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Total Detections
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Average Severity Card */}
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip
                    size="lg"
                    color={getSeverityColor(stats?.average_severity || null)}
                    variant="soft"
                  >
                    {stats?.average_severity ? (stats.average_severity * 100).toFixed(0) : 'N/A'}%
                  </Chip>
                </Box>
                <Typography level="h4">
                  {stats?.average_severity ? stats.average_severity.toFixed(2) : 'N/A'}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Average Severity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Status Distribution Card */}
          <Grid xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>Analysis Status</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(stats?.by_status || {}).map(([status, count]: [string, any]) => (
                    <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        size="sm"
                        color={getStatusColor(status)}
                        variant="soft"
                      >
                        {status}
                      </Chip>
                      <Typography level="body-sm">{count}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Detections by Category */}
          <Grid xs={12}>
            <Card>
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 3 }}>Detections by Category</Typography>
                <Grid container spacing={2}>
                  {Object.entries(stats?.detections_by_category || {}).map(([category, count]: [string, any]) => (
                    <Grid key={category} xs={6} sm={4} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 'sm',
                          backgroundColor: 'background.level1',
                          textAlign: 'center',
                        }}
                      >
                        <Typography level="h4">{count}</Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid xs={12}>
            <Card>
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 3 }}>Quick Actions</Typography>
                <Grid container spacing={2}>
                  <Grid xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      size="lg"
                      variant="outlined"
                      color="primary"
                      startDecorator={<AddIcon />}
                      onClick={() => navigate('/hawkeye/analyze')}
                    >
                      Start New Analysis
                    </Button>
                  </Grid>
                  <Grid xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      size="lg"
                      variant="outlined"
                      color="primary"
                      startDecorator={<HistoryIcon />}
                      onClick={() => navigate('/hawkeye/history')}
                    >
                      View Analysis History
                    </Button>
                  </Grid>
                  <Grid xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      size="lg"
                      variant="outlined"
                      color="primary"
                      startDecorator={<AssessmentIcon />}
                      onClick={() => navigate('/hawkeye/history?status=processing')}
                    >
                      View Active Analyses
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  )
}